import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { botsIaApi } from '../services/api';
import { Bot, Plus, Play, Settings, Trash2, RefreshCw, AlertTriangle, Clock, Zap, FileCode } from 'lucide-react';

const TIPO_BOT_LABEL = {
  inteligencia_municipal: 'Inteligencia Municipal',
  monitor_noticias: 'Monitor Noticias',
  enriquecer_empresas: 'Enriquecer Empresas',
  enriquecer_contactos: 'Enriquecer Contactos',
  resumen_diario_equipo: 'Resumen Diario Equipo',
  alertas_homologacion: 'Alertas Homologación',
  analisis_consumos_cliente: 'Análisis Consumos Cliente',
  propuesta_nuevos_pedidos: 'Propuesta Nuevos Pedidos',
};

const PROVEEDOR_COLOR = {
  gemini: 'bg-blue-100 text-blue-700',
  perplexity: 'bg-violet-100 text-violet-700',
  tavily: 'bg-amber-100 text-amber-700',
  ninguno: 'bg-ink-100 text-ink-500',
};

export default function BotsIA() {
  const [bots, setBots] = useState([]);
  const [plantillas, setPlantillas] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedBot, setExpandedBot] = useState(null);
  const [logs, setLogs] = useState({});
  const [ejecutando, setEjecutando] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([botsIaApi.list(), botsIaApi.plantillas()]);
      setBots(b.items || []);
      setPlantillas(p.plantillas || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const crearDesdePlantilla = async (clave) => {
    try {
      await botsIaApi.crearDesdePlantilla(clave);
      await cargar();
    } catch (e) {
      alert(e.message);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este bot?')) return;
    await botsIaApi.eliminar(id);
    await cargar();
  };

  const toggleActivo = async (bot) => {
    await botsIaApi.actualizar(bot.id, { activo: !bot.activo });
    await cargar();
  };

  const ejecutar = async (bot) => {
    setEjecutando(bot.id);
    try {
      const res = await botsIaApi.ejecutarAhora(bot.id);
      alert(`Ejecución completada.\n\n${res.resumen}\n\n${res.aviso || ''}`);
      await cargar();
      if (expandedBot === bot.id) {
        const l = await botsIaApi.logs(bot.id, 20);
        setLogs({ ...logs, [bot.id]: l.items || [] });
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setEjecutando(null);
    }
  };

  const toggleExpand = async (bot) => {
    if (expandedBot === bot.id) {
      setExpandedBot(null);
    } else {
      setExpandedBot(bot.id);
      const l = await botsIaApi.logs(bot.id, 20);
      setLogs({ ...logs, [bot.id]: l.items || [] });
    }
  };

  return (
    <div className="space-y-6" data-testid="bots-ia-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900 flex items-center gap-3">
            <Bot className="w-7 h-7 text-brand-500" />
            Bots IA programables
          </h1>
          <p className="text-ink-500 mt-1">Configura y programa bots autónomos con Gemini, Perplexity o Tavily. Límites de coste integrados.</p>
        </div>
        <Button variant="outline" onClick={cargar} data-testid="btn-recargar-bots">
          <RefreshCw className="w-4 h-4 mr-2" /> Recargar
        </Button>
      </div>

      {/* Plantillas para crear rápido */}
      <Card className="p-4 bg-brand-50/40 border-brand-200">
        <h3 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
          <FileCode className="w-4 h-4 text-brand-500" /> Crear desde plantilla
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(plantillas).map(([clave, p]) => (
            <div key={clave} className="flex items-center justify-between gap-3 p-3 bg-white border border-ink-200 rounded-lg" data-testid={`plantilla-${clave}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink-900 text-sm truncate">{p.nombre}</div>
                <div className="text-xs text-ink-500 truncate">{p.descripcion}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${PROVEEDOR_COLOR[p.proveedor_ia] || 'bg-ink-100'} text-xs`}>{p.proveedor_ia}</Badge>
                  <span className="text-xs text-ink-400">máx {p.max_coste_estimado_euros}€</span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => crearDesdePlantilla(clave)} data-testid={`btn-crear-${clave}`}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Crear
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Aviso safety */}
      <Card className="p-3 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Ejecuciones controladas.</strong> Cada bot tiene un <code className="bg-white px-1 rounded">max_coste_estimado_euros</code> y <code className="bg-white px-1 rounded">max_items_por_ejecucion</code>.
            Para <strong>testing con 1 provincia</strong>, abre el bot y establece <code>ambito.tipo = "provincias"</code> con <code>provincias: ["08"]</code> (Barcelona) y <code>limite_items: 10</code>.
            Los handlers reales se conectarán en S5+ — por ahora "Ejecutar ahora" registra un log sin consumir créditos.
          </div>
        </div>
      </Card>

      {/* Lista bots */}
      {loading && <div className="text-ink-500">Cargando bots…</div>}
      {!loading && bots.length === 0 && (
        <Card className="p-8 text-center">
          <Bot className="w-10 h-10 mx-auto text-ink-300 mb-3" />
          <p className="text-ink-500">Aún no hay bots. Crea uno desde las plantillas de arriba.</p>
        </Card>
      )}

      <div className="space-y-3" data-testid="bots-list">
        {bots.map((bot) => (
          <Card key={bot.id} className={`p-4 ${bot.activo ? 'border-emerald-200' : ''}`} data-testid={`bot-card-${bot.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-lg ${bot.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-500'} flex items-center justify-center shrink-0`}>
                  <Bot className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-ink-900">{bot.nombre}</h3>
                    <Badge className={`${PROVEEDOR_COLOR[bot.proveedor_ia]} text-xs`}>{bot.proveedor_ia}</Badge>
                    <Badge variant="outline" className="text-xs">{TIPO_BOT_LABEL[bot.tipo] || bot.tipo}</Badge>
                    {bot.activo && <Badge className="bg-emerald-500 text-white text-xs">ACTIVO</Badge>}
                  </div>
                  <p className="text-sm text-ink-500 mt-1">{bot.descripcion}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-ink-500 flex-wrap">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {bot.trigger?.tipo}</span>
                    <span>Ámbito: {bot.ambito?.tipo}{bot.ambito?.limite_items ? ` · máx ${bot.ambito.limite_items} items` : ''}</span>
                    <span>Max coste: {bot.max_coste_estimado_euros}€</span>
                    <span>Ejecuciones: {bot.total_ejecuciones || 0}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => ejecutar(bot)} disabled={ejecutando === bot.id} data-testid={`btn-ejecutar-${bot.id}`}>
                  <Play className="w-3.5 h-3.5 mr-1" /> {ejecutando === bot.id ? '…' : 'Ejecutar ahora'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleActivo(bot)} data-testid={`btn-toggle-${bot.id}`}>
                  <Zap className="w-3.5 h-3.5 mr-1" /> {bot.activo ? 'Desactivar' : 'Activar'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleExpand(bot)} data-testid={`btn-expand-${bot.id}`}>
                  <Settings className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={() => eliminar(bot.id)} data-testid={`btn-eliminar-${bot.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {expandedBot === bot.id && (
              <div className="mt-4 pt-4 border-t border-ink-100 space-y-3 text-sm">
                <div>
                  <div className="text-xs font-semibold text-ink-700 uppercase mb-1">Prompt sistema</div>
                  <pre className="bg-ink-50 p-2 rounded text-xs whitespace-pre-wrap text-ink-700">{bot.prompt_sistema}</pre>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-semibold text-ink-700 uppercase mb-1">Ámbito</div>
                    <pre className="bg-ink-50 p-2 rounded text-xs">{JSON.stringify(bot.ambito, null, 2)}</pre>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-ink-700 uppercase mb-1">Trigger</div>
                    <pre className="bg-ink-50 p-2 rounded text-xs">{JSON.stringify(bot.trigger, null, 2)}</pre>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-ink-700 uppercase mb-1">Últimas ejecuciones</div>
                  {(logs[bot.id] || []).length === 0 && <div className="text-xs text-ink-400">Sin logs todavía.</div>}
                  {(logs[bot.id] || []).map((log, i) => (
                    <div key={i} className="text-xs border-t border-ink-100 py-1.5">
                      <span className="text-ink-500">{new Date(log.fecha_inicio).toLocaleString()}</span> ·{' '}
                      <Badge className={log.estado === 'completado' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>{log.estado}</Badge>{' '}
                      items: {log.items_procesados} · {log.resumen_resultado?.slice(0, 100)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
